import type { TicketListItem } from '../../api/types';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { StatusChip } from '../ticket/StatusChip';
import { TypeIcon } from '../ticket/TypeIcon';

/**
 * Sub-tasks with their own status and a **% Done** rollup (FR-6, README).
 *
 * ⭐ "Done" is read from `status_def.is_final`, not from a hard-coded `code === 'done'`. Statuses are
 * configurable data — a project that adds its own terminal status would otherwise silently report 0%
 * complete forever, and nobody would connect that to a settings change.
 */
export function SubtaskTable({
  subtasks,
  onOpen,
}: {
  subtasks: TicketListItem[];
  onOpen: (ticket: TicketListItem) => void;
}) {
  const vocab = useVocabulary();
  const finalCodes = new Set(vocab.statuses.filter((status) => status.is_final).map((s) => s.code));
  const done = subtasks.filter((task) => finalCodes.has(task.status)).length;
  const percent = subtasks.length === 0 ? 0 : Math.round((done / subtasks.length) * 100);
  // ⚠️ Without the vocabulary we cannot know which statuses are terminal, so we say so rather than
  // printing a confident 0%.
  const rollupKnown = vocab.statuses.length > 0;

  return (
    <section aria-labelledby="subtasks-heading" className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 id="subtasks-heading" className="text-sm font-semibold">
          Sub-tasks <span className="text-[color:var(--color-ink-muted)]">({subtasks.length})</span>
        </h3>
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          {rollupKnown ? `${percent}% Done` : '% Done unavailable'}
        </span>
      </div>

      {rollupKnown ? (
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Sub-tasks complete"
          className="h-1.5 w-full overflow-hidden rounded bg-[color:var(--color-surface-sunken)]"
        >
          <div className="h-full rounded bg-emerald-500" style={{ width: `${percent}%` }} />
        </div>
      ) : null}

      <ul className="divide-y divide-[color:var(--color-line)] rounded border border-[color:var(--color-line)]">
        {subtasks.map((task) => (
          <li key={task.id}>
            <button
              type="button"
              onClick={() => onOpen(task)}
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
            >
              <TypeIcon type={vocab.type(task.type)} size={13} />
              <span className="shrink-0 font-mono text-xs text-[color:var(--color-accent)]">
                {task.key}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm">{task.title}</span>
              <StatusChip status={vocab.status(task.status)} className="max-w-[7rem] shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
